/**
 * Device Session Manager (seat enforcement)
 *
 * Responsibilities:
 *   - Register the current device against the user's subscription on login.
 *   - On a 409 seat_limit_reached, open the "My Devices" manager so the user can
 *     free a seat, then retry registration.
 *   - Run a periodic heartbeat so the server can track liveness and so this
 *     client learns when its seat has been reclaimed elsewhere.
 *   - When the seat is revoked, notify the user and open the device manager.
 *
 * Desktop only — on web there is no device identity and registration is skipped.
 */

import API from './api';
import { getCurrentUserFromStore } from './auth';
import { getDeviceIdentity } from './deviceIdentity';

// Heartbeat cadence. Frequent enough to notice a reclaimed seat reasonably fast,
// infrequent enough to be cheap. Also fires immediately on network reconnect.
const HEARTBEAT_INTERVAL = 30 * 60 * 1000; // 30 minutes

let storeInstance = null;
let unsubscribe = null;
let heartbeatTimer = null;
let lastUserId = null;
let registering = false;

const getBodaProContext = () => window['bodapro-context'] || window.BodaProContext;

function notify(message, intent = 'warning') {
    const ctx = getBodaProContext();
    if (ctx && ctx.showToast) {
        ctx.showToast({ message, intent, timeout: 6000 });
    } else {
        console.warn('[deviceSession]', message);
    }
}

function openDeviceManager(extra = {}) {
    const ctx = getBodaProContext();
    if (ctx && ctx.triggerCommand) {
        ctx.triggerCommand('subscription.devices.manage', extra);
    }
}

/**
 * Register this device; handle the seat-limit response by opening the manager.
 */
async function ensureDeviceRegistered() {
    if (registering) return;
    registering = true;
    try {
        const result = await API.registerDevice();
        if (result && result.skipped) {
            // Web build / no device identity — nothing to enforce.
            return;
        }
        startHeartbeat();
    } catch (error) {
        if (error && error.status === 409) {
            // Seat limit reached — surface the device manager so the user can
            // remove another device, then they can retry from there.
            notify('Device limit reached for this subscription. Remove a device to continue.', 'danger');
            openDeviceManager({
                reason: 'seat_limit_reached',
                seats_total: error.data?.seats_total,
                devices: error.data?.devices || []
            });
        } else {
            console.warn('Device registration failed:', error);
        }
    } finally {
        registering = false;
    }
}

/**
 * Allow the device manager (or 409 dialog) to retry registration after a seat
 * has been freed.
 */
export async function retryDeviceRegistration() {
    return ensureDeviceRegistered();
}

async function doHeartbeat() {
    try {
        const status = await API.heartbeat();
        if (status && status.active === false) {
            stopHeartbeat();
            notify('This device is no longer authorized for your subscription. Re-register to continue.', 'danger');
            openDeviceManager({ reason: status.reason || 'seat_revoked' });
        }
    } catch (error) {
        // Network/transient errors are non-fatal — try again next tick.
        console.debug('Heartbeat failed (will retry):', error?.message);
    }
}

function startHeartbeat() {
    stopHeartbeat();
    heartbeatTimer = setInterval(doHeartbeat, HEARTBEAT_INTERVAL);
}

function stopHeartbeat() {
    if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
    }
}

function handleSessionChange() {
    const user = getCurrentUserFromStore(storeInstance);
    const userId = user?.id || null;

    if (userId && userId !== lastUserId) {
        // Logged in (or switched account) — register this device.
        lastUserId = userId;
        ensureDeviceRegistered();
    } else if (!userId && lastUserId) {
        // Logged out — stop heartbeat.
        lastUserId = null;
        stopHeartbeat();
    }
}

/**
 * Initialize the device session manager. Safe to call once at module init.
 */
export function initDeviceSession(store) {
    if (!store) return;
    storeInstance = store;

    // React to login/logout transitions.
    if (unsubscribe) unsubscribe();
    unsubscribe = store.subscribe(handleSessionChange);

    // Heartbeat promptly when the network comes back.
    window.addEventListener('online', () => {
        if (lastUserId) doHeartbeat();
    });

    // Handle the case where a session is already present at startup
    // (e.g. redux-persist rehydrated a logged-in user).
    handleSessionChange();
}

export default initDeviceSession;
